import { formatMoney } from '@guildite/domain/money';
import { Fragment, useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router';

import { useCurrentUser, useData } from '../data/DataContext.tsx';
import type { ShopCard } from '../data/ports.ts';
import { Avatar } from '../design/primitives.tsx';
import { Waves } from '../design/Waves.tsx';
import { useAccount } from '../features/account/AccountContext.tsx';
import { useCart } from '../features/cart/CartContext.tsx';
import { useNewShopOrders } from '../features/orders/useNewShopOrders.ts';
import { FollowButton } from '../features/social/FollowButton.tsx';
import { useSocialCounts } from '../features/social/counts.ts';
import { useGuildPictures } from '../features/guilds/guildPictures.ts';
import { shopPath } from '../features/shops/MyShopsContext.tsx';
import { UpdatePrompt, useInstallPrompt } from '../platform/pwa.tsx';
import { GIcon, type IconName } from './icons.tsx';
import { OfflineBanner } from './OfflineBanner.tsx';
import { RailExtra, RailProvider } from './rail.tsx';
import { ToastProvider, useToast } from './toast.tsx';
import { ViewerProvider } from '../features/viewer/MediaViewer.tsx';
import styles from './Shell.module.css';
import { SuspendedScreen } from './SuspendedScreen.tsx';

type NavEntry = { to: string; label: string; icon: IconName; end?: boolean; match?: RegExp };

// The five tabs (design navDefs) and the sidebar's extra entries.
const TABS: NavEntry[] = [
  { to: '/', label: 'Home', icon: 'home', end: true, match: /^\/($|g\/|post\/|guild\/|guilds(?:\/|$))/ },
  { to: '/discover', label: 'Discover', icon: 'discover', match: /^\/(discover|p\/|u\/|map)/ },
  { to: '/create', label: 'Create', icon: 'create', match: /^\/(create|shop\/[^/]+\/listings)/ },
  { to: '/orders', label: 'Orders', icon: 'orders', match: /^\/(orders|shop\/[^/]+\/orders|checkout|cart)/ },
  { to: '/me', label: 'Profile', icon: 'profile', match: /^\/(me|settings|payouts|rider|admin)/ },
];

// A shop is its own place, not part of your profile: shop pages (/s/…) and
// shop management (/shops, /shop/…) light no phone tab. Shop orders and
// listings stay under Orders and Create. In the sidebar, "My shops" lights up
// on management pages and on the pages of shops you run.
const SHOP_MANAGEMENT = /^\/(shops$|shop\/)/;
const SHOP_WORK = /^\/shop\/[^/]+\/(orders|listings)(\/|$)/;
const SHOP_PAGE = /^\/s\/([^/]+)/;

function inMyShops(pathname: string, shops: { shopId: string; handle: string | null }[]) {
  if (SHOP_MANAGEMENT.test(pathname)) return !SHOP_WORK.test(pathname);
  const page = SHOP_PAGE.exec(pathname);
  if (!page) return false;
  let key = page[1]!.toLowerCase();
  try { key = decodeURIComponent(key); } catch { /* a malformed link: compare it as it is */ }
  return shops.some(shop => shop.shopId.toLowerCase() === key || shop.handle?.toLowerCase() === key);
}

const MORE: NavEntry[] = [
  { to: '/notifications', label: 'Notifications', icon: 'bell' },
  { to: '/messages', label: 'Messages', icon: 'messages' },
  { to: '/saved', label: 'Saved', icon: 'saved' },
  { to: '/cart', label: 'Cart', icon: 'cart' },
  { to: '/map', label: 'Map', icon: 'map' },
  { to: '/settings', label: 'Settings', icon: 'gear' },
];

// `active` decides the highlight itself (for entries that cover several
// routes); without it the route decides.
function SideLink({ item, badge, active }: { item: NavEntry; badge?: number; active?: boolean }) {
  const content = (isActive: boolean) => (
    <>
      <GIcon name={item.icon} size={24} stroke={isActive ? 2.4 : 1.9} />
      <span className={styles.sideLabel}>{item.label}</span>
      {badge ? <span className={styles.sideBadge} aria-label={`${badge} new`}>{badge}</span> : null}
    </>
  );
  if (active !== undefined) {
    return <Link to={item.to} className={styles.sideLink} title={item.label} aria-current={active ? 'page' : undefined}>{content(active)}</Link>;
  }
  return (
    <NavLink to={item.to} end={item.end} className={styles.sideLink} title={item.label}>
      {({ isActive }) => content(isActive)}
    </NavLink>
  );
}

function RightRail() {
  const { profile, name, shops } = useAccount();
  const { guilds, discover } = useData();
  const cart = useCart();
  const install = useInstallPrompt();
  const toast = useToast();
  const [myGuilds, setMyGuilds] = useState<{ id: string; name: string }[]>([]);
  const [suggested, setSuggested] = useState<ShopCard[]>([]);
  useEffect(() => {
    let active = true;
    guilds.mine().then(list => { if (active) setMyGuilds(list.slice(0, 4)); }, () => undefined);
    discover.shops('').then(list => { if (active) setSuggested(list); }, () => undefined);
    return () => { active = false; };
  }, [guilds, discover]);
  const guildPictures = useGuildPictures(myGuilds.map(guild => guild.id), 68);
  const mine = new Set(shops.map(shop => shop.shopId));
  const shopsToShow = suggested.filter(shop => !mine.has(shop.shopId)).slice(0, 3);
  const subtotal = cart.items.reduce((sum, item) => sum + item.unitMinor * item.quantity, 0);
  const displayName = profile?.name || name;

  return (
    <aside className={styles.rail} aria-label="For you">
      <Link to="/me" className={styles.railMe} aria-label={displayName ? undefined : "Your profile"}>
        <Avatar name={displayName} photoUrl={profile?.photoUrl ?? null} size={48} />
        <span className={styles.railMeText}>
          <span className={styles.railName}>{displayName}</span>
          {profile?.handle ? <span className={styles.railSub}>@{profile.handle}</span> : null}
        </span>
      </Link>
      {shops[0] ? (
        <Link to={shopPath(shops[0])} className={styles.railShop}>
          <Avatar name={shops[0].name} size={36} />
          <span className={styles.railMeText}>
            <span className={styles.railShopName}>{shops[0].name}</span>
            <span className={styles.railSub}>{shops.length > 1 ? `and ${shops.length - 1} more` : 'Your shop'}</span>
          </span>
        </Link>
      ) : null}
      <RailExtra />
      <section className={styles.railCard} aria-label="Your cart">
        <div className={styles.railCardHead}><span className={styles.railCardTitle}>Your cart</span><span className={styles.railSub}>{cart.count ? `${cart.count} ${cart.count === 1 ? 'item' : 'items'}` : 'Empty'}</span></div>
        {cart.items.slice(0, 3).map(item => (
          <div key={item.key} className={styles.railCartRow}>
            {item.photo ? <img src={item.photo} alt="" /> : <span className={styles.railThumb} aria-hidden="true" />}
            <span className={styles.railCartName}>{item.quantity} × {item.name}</span>
            <b>{formatMoney(item.unitMinor * item.quantity)}</b>
          </div>
        ))}
        <Link to={cart.count ? '/cart' : '/discover'} className={[styles.railCartButton, cart.count ? styles.railCartButtonBlue : ''].join(' ')}>
          {cart.count ? `Checkout · ${formatMoney(subtotal)}` : 'Find something you like'}
        </Link>
      </section>
      {myGuilds.length ? (
        <section className={styles.railList} aria-labelledby="rail-guilds">
          <h2 id="rail-guilds" className={styles.railHeading}>YOUR GUILDS</h2>
          {myGuilds.map(guild => (
            <Link key={guild.id} to={`/g/${guild.id}`} className={styles.railRow}>
              <Avatar name={guild.name} photoUrl={guildPictures[guild.id]} size={34} square />
              <span className={styles.railMeText}><span className={styles.railRowName}>{guild.name}</span></span>
            </Link>
          ))}
        </section>
      ) : null}
      {shopsToShow.length ? (
        <section className={styles.railList} aria-labelledby="rail-shops">
          <h2 id="rail-shops" className={styles.railHeading}>SHOPS YOU MIGHT LIKE</h2>
          {shopsToShow.map(shop => (
            <div key={shop.shopId} className={styles.railRow}>
              <Link to={`/s/${encodeURIComponent(shop.handle ?? shop.shopId)}`} className={styles.railRowLink}>
                <Avatar name={shop.name} photoUrl={shop.photoUrl} size={34} />
                <span className={styles.railMeText}>
                  <span className={styles.railRowName}>{shop.handle ?? shop.name}</span>
                  <span className={styles.railSub}>{[shop.category, shop.location.area].filter(Boolean).join(' · ')}</span>
                </span>
              </Link>
              <FollowButton target={{ type: 'shop', id: shop.shopId }} name={shop.name} look="link" />
            </div>
          ))}
        </section>
      ) : null}
      <section className={styles.railApp}>
        <span className={styles.railCardTitle}>Get the app</span>
        <span className={styles.railAppText}>{install ? 'Opens like an app, works offline and gets notifications.' : 'Same account on phones, tablets and computers. Orders, chats and carts stay in sync.'}</span>
        <div className={styles.railAppButtons}>
          <button type="button" className={styles.railStore} onClick={() => toast.show('On iPhone or iPad: open Guildite in Safari, tap Share, then Add to Home Screen. The App Store app is coming.')}>iPhone / iPad</button>
          <button type="button" className={styles.railStore} onClick={() => (install ? void install() : toast.show('On Android: open Guildite in Chrome, tap ⋮, then Install app. The Play Store app is coming.'))}>Android</button>
        </div>
      </section>
      <nav className={styles.railFooter} aria-label="About Guildite">
        <Link to="/about">About</Link><Link to="/settings">Settings</Link><Link to="/help">Help</Link><a href="/help/privacy-policy.html">Privacy</a><a href="/help/terms-of-service.html">Terms</a>
      </nav>
    </aside>
  );
}

function BottomTabs({ ordersBadge }: { ordersBadge: number }) {
  const location = useLocation();
  const { profile } = useAccount();
  // Your own profile (/u/<you>) is the Profile tab's root.
  const pathname = profile?.handle && location.pathname === `/u/${profile.handle}` ? '/me' : location.pathname;
  // Only one tab is lit: a tab's own root first, else the first tab whose
  // pattern matches (/shop/:id/orders is Orders, not also Profile).
  const rootTab = TABS.find(item => pathname === item.to);
  const litTab = rootTab ?? TABS.find(item => item.match?.test(pathname));
  return (
    <nav className={styles.tabs} aria-label="Tabs">
      {TABS.map(item => {
        const root = item === rootTab;
        const inTab = item === litTab;
        return (
          <NavLink key={item.to} to={item.to} end={item.end} className={[styles.tab, inTab ? styles.tabIn : '', root ? styles.tabRoot : ''].join(' ')}>
            <GIcon name={item.icon} size={24} stroke={inTab ? 2.3 : 1.9} />
            <span>{item.label}</span>
            {item.to === '/orders' && ordersBadge ? <span className={styles.tabBadge} aria-label={`${ordersBadge} new`}>{ordersBadge}</span> : null}
          </NavLink>
        );
      })}
    </nav>
  );
}

// The visible window height (--vvh), which shrinks when the phone keyboard
// opens, so chat screens keep their message box right above it. While the
// keyboard is open the page must not stay panned (iPhone).
function useVisibleHeight() {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const root = document.documentElement;
    const update = () => {
      root.style.setProperty('--vvh', `${Math.round(viewport.height)}px`);
      const keyboard = window.innerHeight - viewport.height > 150;
      if (keyboard) root.dataset.keyboard = '';
      else delete root.dataset.keyboard;
      if (root.dataset.chat !== undefined && window.scrollY) window.scrollTo(0, 0);
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => { viewport.removeEventListener('resize', update); viewport.removeEventListener('scroll', update); };
  }, []);
}

export function AppShell() {
  useVisibleHeight();
  const user = useCurrentUser();
  const account = useAccount();
  const location = useLocation();
  const counts = useSocialCounts();
  const cart = useCart();
  const newOrders = useNewShopOrders();

  if (user && account.status === 'suspended') return <SuspendedScreen />;

  // Current Terms and Privacy Policy first (accounts from the old app, or after
  // a policy update), then a username; each is asked once.
  const here = location.pathname + location.search;
  if (user && account.status === 'ready' && account.policies && !account.policies.accepted && location.pathname !== '/policies') {
    return <Navigate to={`/policies?next=${encodeURIComponent(here)}`} replace />;
  }
  if (user && account.status === 'ready' && !account.profile?.handle && location.pathname !== '/welcome' && location.pathname !== '/policies') {
    return <Navigate to={`/welcome?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const badges: Record<string, number> = { '/notifications': counts.notifications, '/messages': counts.messages, '/cart': cart.count, '/orders': newOrders };
  // The admin panel takes a wide column and no side rail (design ADMIN).
  const wide = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  return (
    <RailProvider>
    <ToastProvider>
    <ViewerProvider>
    <div className={[styles.shell, wide ? styles.shellWide : ''].join(' ')}>
      <a className="skip-link" href="#main">Skip to content</a>
      <div className={styles.backdrop} aria-hidden="true"><Waves /></div>
      <nav className={styles.sidebar} aria-label="Main">
        <Link to="/" className={styles.sideWordmark}>guildite</Link>
        <Link to="/" className={styles.sideMark} aria-label="Guildite home">g</Link>
        {TABS.map(item => (
          <Fragment key={item.to}>
            <SideLink item={item} badge={user ? badges[item.to] : 0} />
            {user && item.to === '/discover' ? <SideLink item={{ to: '/guilds', label: 'Guilds', icon: 'guilds' }} /> : null}
            {user && item.to === '/me' && account.shops.length ? <SideLink item={{ to: '/shops', label: 'My shops', icon: 'shop' }} active={inMyShops(location.pathname, account.shops)} /> : null}
          </Fragment>
        ))}
        {user ? MORE.map(item => <SideLink key={item.to} item={item} badge={badges[item.to]} />) : <SideLink item={{ to: '/auth/sign-in', label: 'Sign in', icon: 'profile' }} />}
        <div className={styles.sideSpacer} />
        <p className={styles.sideFooter}>Also on iPhone and Android.<br />© 2026 Gildaig Ltd</p>
      </nav>
      <div className={styles.stage}>
        <main id="main" className={styles.column}>
          <OfflineBanner />
          <UpdatePrompt />
          <Outlet />
        </main>
      </div>
      {user && !wide ? <RightRail /> : null}
      <BottomTabs ordersBadge={newOrders} />
    </div>
    </ViewerProvider>
    </ToastProvider>
    </RailProvider>
  );
}
